import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaGraduationCap,
  FaSearch,
  FaSyncAlt,
  FaUserGraduate,
  FaPlus,
  FaSave,
  FaTrash,
  FaTimes,
  FaEdit,
  FaLock,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function resolveFeeAmount(fee) {
  if (fee?.student_fee !== undefined && fee?.student_fee !== null) {
    return parseFloat(fee.student_fee) || 0;
  }
  if (fee?.special_value !== undefined && fee?.special_value !== null) {
    return parseFloat(fee.special_value) || 0;
  }
  if (fee?.fee_value !== undefined && fee?.fee_value !== null) {
    return parseFloat(fee.fee_value) || 0;
  }
  return 0;
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

/** Locked when fee collected or approved settlement — edit/delete not allowed. */
function isFeeLocked(student, fee, feeCollectionStatus) {
  if (!student || !fee) return true;
  if (isTruthyFlag(fee.fee_collected)) return true;
  const feeKey = `${student.student_id}_${student.class_id}_${fee.fee_id}`;
  if (isTruthyFlag(feeCollectionStatus?.[feeKey])) return true;
  return false;
}

function getStaffId() {
  const staff = SessionManager.getStaffDetails();
  return staff?.staff_id || "";
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const RteStudent = () => {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadClass, setLoadClass] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [feeCollectionStatus, setFeeCollectionStatus] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [editingFee, setEditingFee] = useState(null);
  const [editedFeeValue, setEditedFeeValue] = useState("");
  const [savingFee, setSavingFee] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        setFeeCollectionStatus({});
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const schoolCode = SessionManager.getSchoolCode();
        const res = await axios.get(`${API_BASE()}getRteStudentsBatch.php`, {
          params: {
            session_id: sessionId,
            class_id: classId,
            section_id: sectionId,
            school_code: schoolCode,
            limit: 500,
            offset: 0,
          },
        });
        const payload = res?.data;
        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          const rows = payload.data;
          setStudents(rows);

          const feeStatus = {};
          rows.forEach((student) => {
            const key = `${student.student_id}_${student.class_id}`;
            feeStatus[key] = isTruthyFlag(student.fee_collected);
            if (Array.isArray(student.fee_details)) {
              student.fee_details.forEach((fee) => {
                feeStatus[
                  `${student.student_id}_${student.class_id}_${fee.fee_id}`
                ] = isTruthyFlag(fee.fee_collected);
              });
            }
          });
          setFeeCollectionStatus(feeStatus);
        } else {
          setStudents([]);
          setFeeCollectionStatus({});
          if (payload?.api_remark) {
            myToast.notify_warning(payload.api_remark);
          }
        }
      } catch (error) {
        console.error("[RteStudent] load error", error);
        setStudents([]);
        setFeeCollectionStatus({});
        myToast.notify_warning("Failed to load RTE students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const onClassSelect = useCallback(
    (nextClassId, nextSectionId, nextSessionId, position, className, sectionName) => {
      setSectionId(nextSectionId);
      setSessionId(nextSessionId);
      setClassSectionName(
        nextSectionId === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedClassPosition(position);
      setClassId(nextClassId);
      setSearchTerm("");
      setEditingFee(null);
      setEditedFeeValue("");
      setLoadClass(true);
    },
    []
  );

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const cls = Array.isArray(s.class) ? s.class[0] : null;
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_id,
        s.sr_admission_no,
        cls?.student_roll_no,
        cls?.class_name,
        cls?.section_name,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  const lockedCount = useMemo(() => {
    let count = 0;
    students.forEach((s) => {
      const fees = s.fee_details || [];
      const anyLocked = fees.some((fee) =>
        isFeeLocked(s, fee, feeCollectionStatus)
      );
      if (anyLocked || isTruthyFlag(s.fee_collected)) count += 1;
    });
    return count;
  }, [students, feeCollectionStatus]);

  const handleEdit = useCallback(
    (student, fee) => {
      if (isFeeLocked(student, fee, feeCollectionStatus)) {
        myToast.notify_warning(
          `Cannot edit: Fee collection or settlement is already done for ${fee.fee_name}.`
        );
        return;
      }
      const currentValue = resolveFeeAmount(fee);
      setEditingFee({
        student_id: student.student_id,
        fee_id: fee.fee_id,
        originalValue: currentValue,
      });
      setEditedFeeValue(currentValue);
    },
    [feeCollectionStatus]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingFee(null);
    setEditedFeeValue("");
  }, []);

  const handleSaveFee = useCallback(
    async (student, fee) => {
      if (
        !editingFee ||
        editingFee.student_id !== student.student_id ||
        editingFee.fee_id !== fee.fee_id
      ) {
        return;
      }

      const newValue = parseFloat(editedFeeValue);
      if (isNaN(newValue) || newValue < 0) {
        myToast.notify_warning("Please enter a valid fee amount");
        return;
      }
      if (newValue === editingFee.originalValue) {
        myToast.notify_warning("No changes to save");
        handleCancelEdit();
        return;
      }

      if (isFeeLocked(student, fee, feeCollectionStatus)) {
        myToast.notify_warning(
          `Cannot save: Fee collection or settlement is already done for ${fee.fee_name}.`
        );
        return;
      }

      const staffId = getStaffId();
      const schoolCode = SessionManager.getSchoolCode();
      if (!staffId || !schoolCode) {
        myToast.notify_warning("Staff or school code is missing.");
        return;
      }

      setSavingFee(true);
      try {
        const res = await axios.get(`${API_BASE()}updateRteFee.php`, {
          params: {
            staff_id: staffId,
            student_id: student.student_id,
            session_id: sessionId,
            class_id: student.class_id,
            section_id: sectionId,
            fee_id: fee.fee_id,
            fee_value: newValue,
            fee_value_old: editingFee.originalValue,
            school_code: schoolCode,
          },
        });
        const payload = res?.data;
        if (payload?.api_status === "OK") {
          myToast.notify_success(
            `Fee "${fee.fee_name}" updated successfully`
          );
          handleCancelEdit();
          await loadStudents({ soft: true });
        } else {
          myToast.notify_error(payload?.api_remark || "Failed to update fee");
        }
      } catch (error) {
        console.error("[RteStudent] save fee error", error);
        myToast.notify_error("Error updating fee. Please try again.");
      } finally {
        setSavingFee(false);
      }
    },
    [
      editingFee,
      editedFeeValue,
      feeCollectionStatus,
      sessionId,
      sectionId,
      handleCancelEdit,
      loadStudents,
    ]
  );

  const handleDelete = useCallback(
    async (student, fee) => {
      if (isFeeLocked(student, fee, feeCollectionStatus)) {
        myToast.notify_warning(
          `Cannot delete: Fee collection or settlement is already done for ${fee.fee_name}.`
        );
        return;
      }
      if (
        !window.confirm(
          `Are you sure you want to delete RTE fee "${fee.fee_name}" for ${student.stu_name}?`
        )
      ) {
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      setDeleting(true);
      try {
        const body = new URLSearchParams({
          student_id: String(student.student_id),
          class_id: String(student.class_id),
          section_id: String(sectionId),
          session_id: String(sessionId),
          school_code: String(schoolCode),
          fee_id: String(fee.fee_id),
          status: "InActive",
        });
        const res = await axios.post(
          `${API_BASE()}updateSpecialCaseStatus.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        const payload = res?.data;
        if (payload?.api_status === "OK") {
          myToast.notify_success(
            `RTE fee "${fee.fee_name}" deleted successfully.`
          );
          await loadStudents({ soft: true });
        } else {
          myToast.notify_error(
            payload?.api_remark || "Failed to delete RTE fee."
          );
        }
      } catch (error) {
        console.error("[RteStudent] delete fee error", error);
        myToast.notify_error("Error deleting RTE fee. Please try again.");
      } finally {
        setDeleting(false);
      }
    },
    [feeCollectionStatus, sectionId, sessionId, loadStudents]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const classSelected = Boolean(classId && sectionId && sessionId);
  const busy = loading || refreshing || savingFee || deleting;

  return (
    <div
      className="content payroll-page sm-page rn-page rtes-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> RTE Student</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaGraduationCap />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">RTE Student</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${students.length} RTE student${
                      students.length === 1 ? "" : "s"
                    }`
                  : "Select a class to view RTE fees"}
                {lockedCount > 0 ? ` · ${lockedCount} locked` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="rn-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to="/AdminMain/RteCreation" className="rtec-hero-link">
              <FaPlus aria-hidden /> Add RTE
            </Link>
          </div>
        </header>

        <div className="rn-content">
          <div className="rn-class-wrap">
            <SelectClass
              value={loadClass}
              action={onClassSelect}
              selected_position={selectedClassPosition}
            />
          </div>

          {!classSelected ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section to load RTE students
              </p>
            </div>
          ) : loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <div className="rn-toolbar rn-toolbar--top">
                <div className="rn-toolbar__status" aria-live="polite">
                  <span className="rn-status-chip rn-status-chip--students">
                    {filteredStudents.length} shown
                  </span>
                  {lockedCount > 0 ? (
                    <span className="rn-status-chip rn-status-chip--locked">
                      {lockedCount} locked
                    </span>
                  ) : (
                    <span className="rn-status-chip rn-status-chip--clean">
                      All editable
                    </span>
                  )}
                </div>
                <div className="rn-toolbar__actions">
                  <Link
                    to="/AdminMain/RteCreation"
                    className="rn-btn rn-btn--solid"
                    style={{ textDecoration: "none" }}
                  >
                    <span className="rn-btn__icon" aria-hidden>
                      <FaPlus />
                    </span>
                    <span className="rn-btn__text">
                      <span className="rn-btn__title">Add New RTE</span>
                      <span className="rn-btn__hint">Create RTE fees</span>
                    </span>
                  </Link>
                </div>
              </div>

              {filteredStudents.length === 0 ? (
                <div className="sm-empty">
                  <FaUserGraduate className="rn-empty-icon" aria-hidden />
                  <p className="sm-empty__title">No RTE students found</p>
                  <p className="sm-empty__hint">
                    {searchTerm
                      ? "Try a different search"
                      : "Add RTE students from Add RTE"}
                  </p>
                </div>
              ) : (
                <div className="rtes-list">
                  {filteredStudents.map((item, index) => {
                    const cls = Array.isArray(item.class) ? item.class[0] : null;
                    const classLabel = cls
                      ? cls.section_id === "No"
                        ? cls.class_name
                        : `${cls.class_name || ""}:${cls.section_name || ""}`
                      : item.class_name || "—";
                    const fees = item.fee_details || [];
                    const enrollment =
                      item.student_enrollment_no || item.student_id;

                    return (
                      <article
                        key={`${enrollment}_${item.class_id || index}`}
                        className="rtes-card"
                        style={{ "--rn-i": index }}
                      >
                        <div className="rtes-card__who">
                          <StudentAvatar
                            name={item.stu_name}
                            imagePath={item.student_img_path}
                          />
                          <div className="min-w-0">
                            <div className="rn-row__name">
                              {item.stu_name || "—"}
                            </div>
                            <div className="rn-row__chips">
                              <span className="rn-chip rn-chip--father">
                                {item.father_name
                                  ? `S/O ${item.father_name}`
                                  : "—"}
                              </span>
                              {classLabel ? (
                                <span className="rn-chip rn-chip--class">
                                  {classLabel}
                                </span>
                              ) : null}
                              {cls?.student_roll_no ? (
                                <span className="rn-chip rn-chip--enroll">
                                  Roll: {cls.student_roll_no}
                                </span>
                              ) : null}
                              <span className="rn-chip rn-chip--enroll">
                                ID: {enrollment}
                              </span>
                              {item.sr_admission_no ? (
                                <span className="rn-chip rn-chip--enroll">
                                  SRN: {item.sr_admission_no}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="rtes-fees">
                          {fees.length === 0 ? (
                            <div className="rtes-empty-fees">No fee details</div>
                          ) : (
                            fees.map((fee) => {
                              const feeCollected = isFeeLocked(
                                item,
                                fee,
                                feeCollectionStatus
                              );
                              const canEditDelete = !feeCollected;
                              const isEditing =
                                canEditDelete &&
                                editingFee &&
                                editingFee.student_id === item.student_id &&
                                editingFee.fee_id === fee.fee_id;
                              const isRteFee = fee.special_case === "Yes";
                              const displayValue = resolveFeeAmount(fee);
                              const classFee = parseFloat(fee.fee_value) || 0;

                              return (
                                <div
                                  key={fee.fee_id}
                                  className={`rtes-fee${
                                    isRteFee ? " rtes-fee--rte" : ""
                                  }${feeCollected ? " rtes-fee--locked" : ""}${
                                    isEditing ? " rtes-fee--editing" : ""
                                  }`}
                                >
                                  <div className="rtes-fee__identity">
                                    <span className="rtes-fee__name">
                                      {isRteFee
                                        ? `${fee.fee_name} (RTE)`
                                        : fee.fee_name}
                                    </span>
                                    <span className="rtes-fee__class">
                                      Class ₹{classFee.toLocaleString("en-IN")}
                                    </span>
                                  </div>

                                  {isEditing ? (
                                    <div className="rtes-fee__input">
                                      <span aria-hidden>₹</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputMode="numeric"
                                        autoFocus
                                        value={
                                          editedFeeValue === null ||
                                          editedFeeValue === undefined
                                            ? ""
                                            : editedFeeValue
                                        }
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === "" || value === null) {
                                            setEditedFeeValue("");
                                          } else {
                                            const numValue = parseFloat(value);
                                            if (
                                              !isNaN(numValue) &&
                                              numValue >= 0
                                            ) {
                                              setEditedFeeValue(numValue);
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            handleSaveFee(item, fee);
                                          } else if (e.key === "Escape") {
                                            handleCancelEdit();
                                          }
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="rtes-fee__amount">
                                      ₹{displayValue.toLocaleString("en-IN")}
                                    </div>
                                  )}

                                  <div className="rtes-fee__actions">
                                    {!canEditDelete ? (
                                      <span className="rtes-fee__locked-tag">
                                        <FaLock aria-hidden /> Locked
                                      </span>
                                    ) : isEditing ? (
                                      <>
                                        <button
                                          type="button"
                                          className="rtes-btn rtes-btn--save"
                                          onClick={() =>
                                            handleSaveFee(item, fee)
                                          }
                                          disabled={savingFee}
                                        >
                                          <FaSave aria-hidden />
                                          {savingFee ? "…" : "Save"}
                                        </button>
                                        <button
                                          type="button"
                                          className="rtes-btn rtes-btn--cancel"
                                          onClick={handleCancelEdit}
                                          disabled={savingFee}
                                        >
                                          <FaTimes aria-hidden />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="rtes-btn rtes-btn--edit"
                                          onClick={() => handleEdit(item, fee)}
                                          disabled={busy}
                                          title={`Edit ${fee.fee_name}`}
                                        >
                                          <FaEdit aria-hidden />
                                        </button>
                                        {isRteFee ? (
                                          <button
                                            type="button"
                                            className="rtes-btn rtes-btn--delete"
                                            onClick={() =>
                                              handleDelete(item, fee)
                                            }
                                            disabled={busy}
                                            title={`Delete ${fee.fee_name}`}
                                          >
                                            <FaTrash aria-hidden />
                                          </button>
                                        ) : null}
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RteStudent;
